import { CommonModule } from '@angular/common'
import { Router, RouterLink } from '@angular/router'
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  FormsModule,
} from '@angular/forms'
import {
  Component,
  Input,
  model,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  inject,
} from '@angular/core'
import { startWith, map } from 'rxjs/operators'
import { HeaderComponent } from '../header/header.component'
import { MatCardModule } from '@angular/material/card'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatRadioModule } from '@angular/material/radio'
import { MatIconModule } from '@angular/material/icon'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatTableModule } from '@angular/material/table'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling'
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask'
import {
  MtxTooltipModule,
  TooltipPosition,
} from '@ng-matero/extensions/tooltip'
import { Observable, of, combineLatest } from 'rxjs'
import {
  SrecRegistration,
  Address,
  Meter,
  MeterMan,
  MeterMod,
  SupportingFile,
  OtherUtilities,
  UtilityCompany,
  Utilities,
} from '../models/srec-form-request.model'
import { SrecFormService } from '../services/srec-form.service'
import { TermsOfServiceComponent } from '../terms-of-service/terms-of-service.component'
import { PdfViewerModule } from 'ng2-pdf-viewer'
import { HttpServiceHelper } from '../common/http-service-helper'
import { meterMappings } from '../models/meter-mapping.model'
import { ActivatedRoute } from '@angular/router'

export interface MonitoringService {
  serviceName: string
}

const MONITORING_SERVICES: MonitoringService[] = [
  { serviceName: 'Enphase' },
  { serviceName: 'Also Energy' },
  { serviceName: 'SolarEdge' },
]

export interface SupportingDocsContext {
  formType: string
  description: string
  exampleImage: string
}

const SUPPORTING_DOCS_INFO: SupportingDocsContext[] = [
  {
    formType: 'PTO - PGE',
    description:
      'Permission to Operate your solar system from your Utility company - PGE Example',
    exampleImage: '/assets/PGE PTO Example.png',
  },
  {
    formType: 'PTO - SCE',
    description:
      'Permission to Operate your solar system from your Utility company - SCE Example',
    exampleImage: '/assets/SCE PTO Example.png',
  },
  {
    formType: 'PTO - SDG&E',
    description:
      'Permission to Operate your solar system from your Utility company - SDG&E Example',
    exampleImage: '/assets/SDGE PTO Example.png',
  },
  {
    formType: 'Meter Pictures',
    description: 'Photos of your solar meter showing the serial number',
    exampleImage: '/assets/locus-meter-example.jpg',
  },
  {
    formType: 'Interconnection Agreement',
    description:
      'Solar Interconnection Agreement for interconnecting your solar system with your Utility company',
    exampleImage: '/assets/Sample-Interconnection-Agreement.png',
  },
  {
    formType: 'One Line Diagram',
    description: 'Approved electrical One Line diagram of your solar system',
    exampleImage: '/assets/Sample-Design-Document.png',
  },
  {
    formType: 'Ownership Agreement',
    description:
      'Documentation to prove your ownership of the solar system on your property',
    exampleImage: '/assets/loan-agreement-info.png',
  },
]

@Component({
  selector: 'form-home-marketing-desk',
  standalone: true,
  templateUrl: './form-marketing-desk.component.html',
  styleUrl: './form-marketing-desk.component.scss',
  imports: [
    HeaderComponent,
    MatButtonModule,
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatIconModule,
    RouterLink,
    MatProgressSpinnerModule,
    NgxMaskDirective,
    NgxMaskPipe,
    FormsModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatTooltipModule,
    MtxTooltipModule,
    MatTableModule,
    MatProgressSpinnerModule,
    ScrollingModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PdfViewerModule,
  ],
  providers: [provideNgxMask(), SrecFormService, HttpServiceHelper],
})
export class FormMarketingDeskComponent implements OnInit, OnChanges {
  @Input()
  showForm: boolean = false

  @Input()
  referralSource: string | null = null

  @Input()
  isCommercial: boolean = false

  @Input()
  isExistingInstaller: boolean = false

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport | undefined

  isSubmitLoading: boolean = false
  isSubmitFailure: boolean = false
  clientIPAddress: string = ''

  displayedColumns: string[] = ['formType', 'description', 'exampleImage']
  dataSource = SUPPORTING_DOCS_INFO

  position: TooltipPosition = 'after'

  utilityFormControl = new FormControl<string | UtilityCompany>('', [
    Validators.required,
  ])

  monitoringServiceFormControl = new FormControl<string | MonitoringService>('')

  meterManufacturerFormControl = new FormControl<string | { value: string }>('')

  meterModelFormControl = new FormControl<string | { value: string }>('')

  meterManData = MeterMan
  meterModData = MeterMod

  // TODO: update to use enum
  utilities: UtilityCompany[] = Utilities
  otherUtilities: UtilityCompany[] = OtherUtilities

  filteredUtilities: Observable<UtilityCompany[]>
  filteredMonitoringServices: Observable<MonitoringService[]>
  filteredMeterManufacturers: Observable<any[]>
  filteredMeterModels: Observable<any[]>

  files: File[] = []
  supportingFiles: SupportingFile[] = []
  tagTypes: string[] = [
    'PTO',
    'Meter Pictures',
    'Interconnection agreement',
    'One Line Diagram',
    'Ownership Agreement',
    'Other',
  ]
  fileTagTypeFormControl = new FormControl<string>('')
  supportingFileForm: FormGroup
  displayedSuppFileColumns: string[] = ['fileName', 'tagType', 'action']
  allowedTypes = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/svg+xml',
  ]

  disabled = model(false)
  srecForm!: FormGroup
  isTOSClicked: boolean = false
  readonly dialog = inject(MatDialog)

  constructor(
    private formBuilder: FormBuilder,
    private _srecService: SrecFormService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    this.supportingFileForm = this.formBuilder.group({
      fileName: new FormControl(''),
      tagType: new FormControl(''),
    })

    this.filteredUtilities = this.utilityFormControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        // Don't filter when user is typing after "Other - "
        if (typeof value === 'string' && value.startsWith('Other - ')) {
          return ''
        }
        return typeof value === 'string' ? value : value?.utilityName
      }),
      map((name) =>
        name
          ? this._filterUtilities(name)
          : [...this.utilities, ...this.otherUtilities].slice()
      )
    )

    this.filteredMonitoringServices =
      this.monitoringServiceFormControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          // Don't filter when user is typing after "Other - "
          if (typeof value === 'string' && value.startsWith('Other - ')) {
            return []
          }
          const serviceName =
            typeof value === 'string' ? value : value?.serviceName
          return serviceName
        }),
        map((name) => {
          if (!name) {
            return MONITORING_SERVICES.slice()
          }
          return this._filterMonitoringServices(name as string)
        })
      )

    this.filteredMeterManufacturers =
      this.meterManufacturerFormControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          // Don't filter when user is typing after "Other - "
          if (typeof value === 'string' && value.startsWith('Other - ')) {
            return []
          }
          const name = typeof value === 'string' ? value : value?.value
          return name
            ? this._filterMeterManufacturers(name as string)
            : this.meterManData.slice()
        })
      )

    this.filteredMeterModels = combineLatest([
      this.meterModelFormControl.valueChanges.pipe(startWith('')),
      this.meterManufacturerFormControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([modelValue, manufacturerValue]) => {
        // Don't filter when user is typing after "Other - "
        if (
          typeof modelValue === 'string' &&
          modelValue.startsWith('Other - ')
        ) {
          return []
        }
        const manufacturer =
          typeof manufacturerValue === 'object'
            ? manufacturerValue?.value
            : manufacturerValue
        const model =
          typeof modelValue === 'string' ? modelValue : modelValue || ''
        return this._filterMeterModels(model as string, manufacturer || '')
      })
    )

    // Subscribe to manufacturer changes to update model options
    this.meterManufacturerFormControl.valueChanges.subscribe((manufacturer) => {
      const manufacturerValue =
        typeof manufacturer === 'object' ? manufacturer?.value : manufacturer
      this.meterModelFormControl.setValue('')
      this.filteredMeterModels = this.meterModelFormControl.valueChanges.pipe(
        startWith(''),
        map((modelValue) => {
          const model =
            typeof modelValue === 'string' ? modelValue : modelValue || ''
          return this._filterMeterModels(
            model as string,
            manufacturerValue || ''
          )
        })
      )
    })
  }

  ngOnInit() {
    this.initSrecForm(this.formBuilder)

    // Read query params if present
    this.route.queryParamMap.subscribe((params) => {
      const installerParam = params.get('isExistingInstaller')
      const existingResidentialPartnerParam = params.get(
        'isExistingResidentialPartner'
      )

      if (installerParam === 'true') {
        this.isExistingInstaller = true
        this.srecForm.patchValue({
          ciInfo: { customerType: 'existingPartner' },
        })
      } else if (existingResidentialPartnerParam === 'true') {
        this.srecForm.patchValue({
          ciInfo: { customerType: 'existingResidentialPartner' },
        })
      }
    })

    if (this.isCommercial) {
      this.srecForm.get('tosCheckbox')?.clearValidators()
      this.srecForm.get('tosCheckbox')?.updateValueAndValidity()
    }

    if (this.referralSource) {
      this.srecForm.patchValue({
        serviceInfo: { referralSource: this.referralSource },
      })
    }

    // If isExistingInstaller was set via input (not via query param)
    if (this.isExistingInstaller) {
      this.srecForm.patchValue({
        ciInfo: { customerType: 'existingPartner' },
      })
    }

    this._srecService.getClientIPAddress().subscribe((response) => {
      if (response.IPv4) {
        this.clientIPAddress = response.IPv4 as string
      }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['referralSource'] && this.srecForm) {
      this.srecForm.patchValue({
        serviceInfo: { referralSource: this.referralSource },
      })
    }
  }

  fileUploaded: boolean = false

  initSrecForm(formBuilder: FormBuilder) {
    this.srecForm = formBuilder.group({
      ciInfo: new FormGroup({
        customerType: new FormControl(
          '',
          this.isCommercial ? [Validators.required] : []
        ),
        partnerCompanyName: new FormControl(''),
        siteOwnerCompany: new FormControl(''),
        formSubmitterName: new FormControl(''),
      }),
      contactInfo: new FormGroup({
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        phoneNumber: new FormControl('', [
          Validators.required,
          Validators.pattern('^[0-9]{10}$'),
        ]),
        phoneNumberType: new FormControl('', [Validators.required]),
        streetAddress: new FormControl('', [Validators.required]),
        city: new FormControl('', [Validators.required]),
        state: new FormControl('', [Validators.required]),
        zipCode: new FormControl('', [
          Validators.required,
          Validators.pattern('^[0-9]{5}$'),
        ]),
        country: new FormControl({
          value: 'United States',
          disabled: true,
        }),
        emailAddress: new FormControl('', [
          Validators.required,
          Validators.email,
        ]),
        isSystemOwner: new FormControl(false),
      }),
      serviceInfo: new FormGroup({
        isServiceSame: new FormControl(false),
        serviceStreetAddress: new FormControl('', [Validators.required]),
        serviceCity: new FormControl('', [Validators.required]),
        serviceState: new FormControl('', [Validators.required]),
        serviceZipCode: new FormControl('', [
          Validators.required,
          Validators.pattern('^[0-9]{5}$'),
        ]),
        serviceCountry: new FormControl({
          value: 'United States',
          disabled: true,
        }),
        utility: this.utilityFormControl,
        otherUtilityName: new FormControl(''),
        referralSource: new FormControl(''),
        loanType: new FormControl(''),
        ptoDate: new FormControl('', [this.dateValidator]),
        acCapacity: new FormControl('', [Validators.min(0)]),
        dcCapacity: new FormControl('', [Validators.min(0)]),
      }),
      meterInfo: new FormGroup({
        meterSerialNumber: new FormControl(''),
        nodeId: new FormControl(''),
        meterManufacturer: this.meterManufacturerFormControl,
        meterModel: this.meterModelFormControl,
        monitoringService: this.monitoringServiceFormControl,
      }),
      uploadFiles: new FormControl(''),
      addtlComment: new FormControl(''),
      tosCheckbox: new FormControl(
        { value: false, disabled: !this.isTOSClicked },
        this.isCommercial ? [] : [Validators.required, Validators.requiredTrue]
      ),
    })

    // Add dynamic validators based on customer type selection
    this.srecForm
      .get('ciInfo.customerType')
      ?.valueChanges.subscribe((value) => {
        const partnerCompanyControl = this.srecForm.get(
          'ciInfo.partnerCompanyName'
        )
        const siteOwnerCompanyControl = this.srecForm.get(
          'ciInfo.siteOwnerCompany'
        )
        const formSubmitterControl = this.srecForm.get(
          'ciInfo.formSubmitterName'
        )

        // Reset validators and values
        partnerCompanyControl?.clearValidators()
        siteOwnerCompanyControl?.clearValidators()
        formSubmitterControl?.clearValidators()

        // Add validators based on selection
        if (
          value === 'existingPartner' ||
          value === 'existingResidentialPartner'
        ) {
          partnerCompanyControl?.setValidators([Validators.required])
        } else if (value === 'installer') {
          siteOwnerCompanyControl?.setValidators([Validators.required])
          formSubmitterControl?.setValidators([Validators.required])
        }

        // Update validity
        partnerCompanyControl?.updateValueAndValidity()
        siteOwnerCompanyControl?.updateValueAndValidity()
        formSubmitterControl?.updateValueAndValidity()
      })

    // Add validator for otherUtilityName when utility is 'other'
    this.utilityFormControl.valueChanges.subscribe((value) => {
      const otherUtilityControl = this.srecForm.get(
        'serviceInfo.otherUtilityName'
      )
      if (value === 'other') {
        // When "Other" is selected, set the value to "Other - " to allow user input
        setTimeout(() => {
          this.utilityFormControl.setValue('Other - ', { emitEvent: false })
          // Focus the input and move cursor to the end
          const input = document.querySelector(
            'input[formControlName="utility"]'
          ) as HTMLInputElement
          if (input) {
            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
          }
        })
      } else {
        otherUtilityControl?.clearValidators()
      }
      otherUtilityControl?.updateValueAndValidity()
    })

    // Handle monitoring service "Other" selection
    this.monitoringServiceFormControl.valueChanges.subscribe((value) => {
      if (value === 'other') {
        // When "Other" is selected, set the value to "Other - " to allow user input
        setTimeout(() => {
          this.monitoringServiceFormControl.setValue('Other - ', {
            emitEvent: false,
          })
          // Focus the input and move cursor to the end
          const input = document.querySelector(
            'input[formControlName="monitoringService"]'
          ) as HTMLInputElement
          if (input) {
            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
          }
        })
      }
    })

    // Handle meter manufacturer "Other" selection
    this.meterManufacturerFormControl.valueChanges.subscribe((value) => {
      if (value === 'other') {
        // When "Other" is selected, set the value to "Other - " to allow user input
        setTimeout(() => {
          this.meterManufacturerFormControl.setValue('Other - ')
          // Focus the input and move cursor to the end
          const input = document.querySelector(
            'input#meterManufacturer'
          ) as HTMLInputElement
          if (input) {
            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
          }
        })
      }
    })

    // Handle meter model "Other" selection
    this.meterModelFormControl.valueChanges.subscribe((value) => {
      if (value === 'other') {
        // When "Other" is selected, set the value to "Other - " to allow user input
        setTimeout(() => {
          this.meterModelFormControl.setValue('Other - ')
          // Focus the input and move cursor to the end
          const input = document.querySelector(
            'input#meterModel'
          ) as HTMLInputElement
          if (input) {
            input.focus()
            input.setSelectionRange(input.value.length, input.value.length)
          }
        })
      }
    })
  }

  onSubmit(e: Event) {
    if (this.srecForm.invalid) return

    this.isSubmitLoading = true

    const formValues = this.srecForm.value

    // Handle utility value for form submission
    let utilityValue =
      typeof formValues.serviceInfo.utility === 'string'
        ? formValues.serviceInfo.utility
        : formValues.serviceInfo.utility?.utilityName || ''

    // If it's an "Other" utility, clean up the value
    if (utilityValue.startsWith('Other - ')) {
      utilityValue = utilityValue.substring(8).trim()
      if (!utilityValue) {
        this.utilityFormControl.setErrors({ required: true })
        this.isSubmitLoading = false
        return
      }
    }

    // Handle meter manufacturer value for form submission
    let meterManufacturerValue =
      typeof formValues.meterInfo.meterManufacturer === 'string'
        ? formValues.meterInfo.meterManufacturer
        : formValues.meterInfo.meterManufacturer?.value || ''

    // If it's an "Other" meter manufacturer, clean up the value
    if (meterManufacturerValue.startsWith('Other - ')) {
      meterManufacturerValue = meterManufacturerValue.substring(8).trim()
    }

    // Handle meter model value for form submission
    let meterModelValue =
      typeof formValues.meterInfo.meterModel === 'string'
        ? formValues.meterInfo.meterModel
        : formValues.meterInfo.meterModel?.value || ''

    // If it's an "Other" meter model, clean up the value
    if (meterModelValue.startsWith('Other - ')) {
      meterModelValue = meterModelValue.substring(8).trim()
    }

    // Handle monitoring service value for form submission
    let monitoringServiceValue =
      typeof formValues.meterInfo.monitoringService === 'string'
        ? formValues.meterInfo.monitoringService
        : formValues.meterInfo.monitoringService?.serviceName || ''

    // If it's an "Other" monitoring service, clean up the value
    if (monitoringServiceValue.startsWith('Other - ')) {
      monitoringServiceValue = monitoringServiceValue.substring(8).trim()
    }

    const refNumber = this.generateReferenceNumber()

    let model: SrecRegistration = new SrecRegistration(
      formValues.contactInfo.firstName,
      formValues.contactInfo.lastName,
      new Address(
        formValues.contactInfo.streetAddress,
        formValues.contactInfo.city,
        formValues.contactInfo.state,
        formValues.contactInfo.zipCode,
        formValues.contactInfo.country
      ),
      formValues.contactInfo.emailAddress,
      formValues.serviceInfo.referralSource,
      formValues.contactInfo.phoneNumber,
      formValues.contactInfo.phoneNumberType,
      formValues.serviceInfo.loanType,
      utilityValue,
      formValues.serviceInfo.isServiceSame
        ? new Address(
            formValues.contactInfo.streetAddress,
            formValues.contactInfo.city,
            formValues.contactInfo.state,
            formValues.contactInfo.zipCode,
            formValues.contactInfo.country
          )
        : new Address(
            formValues.serviceInfo.serviceStreetAddress,
            formValues.serviceInfo.serviceCity,
            formValues.serviceInfo.serviceState,
            formValues.serviceInfo.serviceZipCode,
            formValues.serviceInfo.serviceCountry
          ),
      new Meter(
        formValues.meterInfo.meterSerialNumber,
        formValues.meterInfo.nodeId,
        meterManufacturerValue,
        meterModelValue,
        monitoringServiceValue
      ),
      formValues.addtlComment,
      this.clientIPAddress,
      new Date().toISOString(),
      this.supportingFiles as SupportingFile[],
      this.files as File[],
      refNumber,
      this.isCommercial
        ? {
            customerType: formValues.ciInfo.customerType,
            partnerCompanyName: formValues.ciInfo.partnerCompanyName,
            siteOwnerCompany: formValues.ciInfo.siteOwnerCompany,
            formSubmitterName: formValues.ciInfo.formSubmitterName,
            ptoDate: formValues.serviceInfo.ptoDate,
            acCapacity: formValues.serviceInfo.acCapacity,
            dcCapacity: formValues.serviceInfo.dcCapacity,
          }
        : undefined
    )
    let formData = new FormData()
    formData.append('data', JSON.stringify(model))
    Array.from(this.files).forEach((file) => {
      formData.append('files', file)
    })
    this.postForm(formData).subscribe({
      next: (success) => {
        this.isSubmitLoading = false
        this.router.navigate(['/success-marketing-desk'], {
          queryParams: { refNumber: refNumber },
        })
      },
      error: (error) => {
        this.isSubmitLoading = false
        this.isSubmitFailure = true
        console.error('API error:', error)
      },
    })
  }

  postForm(form: FormData): Observable<any> {
    return this._srecService.postSrecForm(form)
  }

  onFileChange(event: any): void {
    const uploadedFiles = event.target.files
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i]

      if (
        this.allowedTypes.includes(file.type) &&
        file.size < 25 * 1024 * 1024
      ) {
        this.files.push(file)
        const newIndex = this.supportingFiles.length
        this.supportingFiles.push({
          fileName: file.name,
          tagType: 'Other', // default file type
        })
        this.supportingFileForm.addControl(
          `tagType${newIndex}`,
          this.formBuilder.control('Other')
        )
      } else {
        alert(
          'Invalid file type or File exceeds maximum size of 25MB. Accepted file types: JPEG, PNG, SVG, PDF, TXT, DOC'
        )
      }
    }

    this.fileUploaded = this.supportingFiles.length > 0

    // Trigger change detection
    this.cdr.detectChanges()
  }

  deleteFile(index: number): void {
    this.supportingFiles.splice(index, 1)
    this.files.splice(index, 1)
    this.supportingFileForm.removeControl('tagType' + index)
    this.supportingFiles.length > 0
      ? (this.fileUploaded = true)
      : (this.fileUploaded = false)

    // Reset the file input
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  onTagTypeChange(event: any, index: number): void {
    // update tag type in Supporting Files Array
    let file = this.supportingFiles.find(
      (file) => this.supportingFiles.indexOf(file) === index
    )
    if (file) {
      file.tagType = event.value
    }
  }

  onServiceCheckboxChange(event: any) {
    if (event.checked) {
      this.srecForm.get('serviceInfo.serviceStreetAddress')?.disable()
      this.srecForm.get('serviceInfo.serviceCity')?.disable()
      this.srecForm.get('serviceInfo.serviceState')?.disable()
      this.srecForm.get('serviceInfo.serviceZipCode')?.disable()
      this.srecForm.get('serviceInfo.serviceCountry')?.disable()
    } else {
      this.srecForm.get('serviceInfo.serviceStreetAddress')?.enable()
      this.srecForm.get('serviceInfo.serviceCity')?.enable()
      this.srecForm.get('serviceInfo.serviceState')?.enable()
      this.srecForm.get('serviceInfo.serviceZipCode')?.enable()
      this.srecForm.get('serviceInfo.serviceCountry')?.enable()
    }
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.srecForm.controls
  }

  displayFilteredUtilities(utility: UtilityCompany | string): string {
    if (typeof utility === 'string') {
      return utility
    }
    return utility && utility.utilityName ? utility.utilityName : ''
  }

  private _filterUtilities(value: string | UtilityCompany): UtilityCompany[] {
    // Don't show dropdown options when user is typing after "Other - "
    if (typeof value === 'string' && value.startsWith('Other - ')) {
      return []
    }
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.utilityName.toLowerCase()
    const allUtilities = [...this.utilities, ...this.otherUtilities]
    return allUtilities.filter((option) =>
      option.utilityName.toLowerCase().includes(filterValue)
    )
  }

  displayFilteredMonitoringServices(
    service: MonitoringService | string
  ): string {
    if (typeof service === 'string') {
      return service
    }
    return service && service.serviceName ? service.serviceName : ''
  }

  private _filterMonitoringServices(
    value: string | MonitoringService
  ): MonitoringService[] {
    // Don't show dropdown options when user is typing after "Other - "
    if (typeof value === 'string' && value.startsWith('Other - ')) {
      return []
    }
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.serviceName.toLowerCase()
    return MONITORING_SERVICES.filter((option) =>
      option.serviceName.toLowerCase().includes(filterValue)
    )
  }

  displayFilteredMeterManufacturers(
    meterManufacturer:
      | {
          value: string
        }
      | string
  ): string {
    if (typeof meterManufacturer === 'string') {
      return meterManufacturer
    }
    return meterManufacturer && meterManufacturer.value
      ? meterManufacturer.value
      : ''
  }

  private _filterMeterManufacturers(name: string): { value: string }[] {
    const filterValue = name.toLowerCase()

    return this.meterManData.filter((option) =>
      option.value.toLowerCase().includes(filterValue)
    )
  }

  private _filterMeterModels(
    value: string | undefined,
    manufacturer: string | undefined
  ): string[] {
    const filterValue = value?.toLowerCase() || ''
    const models = manufacturer
      ? meterMappings
          .filter((mapping) => mapping.manufacturer === manufacturer)
          .map((mapping) => mapping.model)
      : meterMappings.map((mapping) => mapping.model)
    return models.filter((model) => model.toLowerCase().includes(filterValue))
  }

  displayFilteredMeterModels(meterModel: { value: string } | string): string {
    if (typeof meterModel === 'string') {
      return meterModel
    }
    return meterModel && meterModel.value ? meterModel.value : ''
  }

  openChange($event: boolean) {
    if ($event) {
      this.cdkVirtualScrollViewPort?.scrollToIndex(0)
      this.cdkVirtualScrollViewPort?.checkViewportSize()
    } else {
    }
  }

  generateReferenceNumber(): string {
    const timestamp = Date.now().toString().slice(-6) // Last 6 digits of timestamp
    const randomString = Math.random().toString(36).substr(2, 4).toUpperCase() // 4 random alphanumeric chars
    return `${timestamp}${randomString}`
  }

  dateValidator(control: any) {
    if (!control.value) {
      return null // Allow empty values (field is optional)
    }

    const dateValue = new Date(control.value)
    const isValidDate = dateValue instanceof Date && !isNaN(dateValue.getTime())

    if (!isValidDate) {
      return { invalidDate: true }
    }

    return null
  }

  onTOSClick(): void {
    this.srecForm.controls['tosCheckbox'].enable()

    const dialogRef = this.dialog.open(TermsOfServiceComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '80vh',
      width: '60vw',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.srecForm.controls['tosCheckbox'].setValue(true)
      }
    })
  }
}
